import { Check, Plus, Split, TriangleAlert, X } from 'lucide-react';
import { useCallback, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui/searchable-select';
import type { CatalogItem } from './api-parity';
import { loadCatalog, type CatalogKind } from './catalog-source';
import {
  formatBp,
  MAX_PROJECTS,
  sharesState,
  splitEqually,
  type Allocation,
  type ProjectShare,
} from './project-shares';

export type CatalogList = {
  readonly status: 'loading' | 'ready' | 'error';
  readonly items: readonly CatalogItem[];
  readonly retry: () => void;
};

/** One of the organization's lists, read when the form opens (#915 C3). */
export function useCatalogList(kind: CatalogKind, open: boolean): CatalogList {
  const [state, setState] = useState<Omit<CatalogList, 'retry'>>({ status: 'loading', items: [] });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!open) return;
    let live = true;
    setState({ status: 'loading', items: [] });
    void loadCatalog(kind).then((out) => {
      if (!live) return;
      setState(out.ok ? { status: 'ready', items: out.value } : { status: 'error', items: [] });
    });
    return () => {
      live = false;
    };
  }, [kind, open, attempt]);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { ...state, retry };
}

/** The list's entries, plus the one already chosen when the list no longer holds it (archived). */
function optionsFor(
  items: readonly CatalogItem[],
  chosen: { id: string; code: string; name: string } | null,
  taken: readonly string[],
): SearchableSelectOption[] {
  const listed = items.filter((item) => item.id === chosen?.id || !taken.includes(item.id));
  const all =
    chosen && chosen.id !== '' && !listed.some((item) => item.id === chosen.id)
      ? [{ id: chosen.id, code: chosen.code, name: chosen.name }, ...listed]
      : listed;
  return all.map((item) => ({ value: item.id, primaryLabel: `${item.code} · ${item.name}` }));
}

function Section({
  title,
  count,
  children,
}: {
  readonly title: string;
  readonly count: string;
  readonly children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-2 rounded-md border p-3">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold">{title}</h3>
        <span className="text-muted-foreground text-xs">{count}</span>
      </div>
      {children}
    </section>
  );
}

function SharesLine({ projects }: { readonly projects: readonly ProjectShare[] }) {
  const { t } = useTranslation('expenses-parity');
  const state = sharesState(projects);
  if (projects.length < 2) return null;
  if (state.kind === 'ok') {
    return (
      <p className="flex items-center gap-1 text-sm">
        <Check className="size-4" aria-hidden />
        {t('projects.total', { total: formatBp(state.totalBp) })}
      </p>
    );
  }
  const message =
    state.kind === 'sum'
      ? t(state.missingBp > 0 ? 'projects.missing' : 'projects.over', {
          total: formatBp(state.totalBp),
          gap: formatBp(Math.abs(state.missingBp)),
        })
      : t(state.kind === 'format' ? 'projects.format' : 'projects.unchosen');
  return (
    <p role="alert" className="text-destructive flex items-center gap-1 text-sm">
      <TriangleAlert className="size-4" aria-hidden />
      {message}
    </p>
  );
}

/** «Progetti»: 0–3, one is a fixed 100 %, two or three take typed shares (U1 board 1.1, 1.2). */
export function ProjectsField({
  allocation,
  list,
  onChange,
}: {
  readonly allocation: Allocation;
  readonly list: CatalogList;
  readonly onChange: (projects: ProjectShare[]) => void;
}) {
  const { t } = useTranslation('expenses-parity');
  const id = useId();
  const projects = allocation.projects;
  const set = (index: number, patch: Partial<ProjectShare>): void =>
    onChange(projects.map((p, i) => (i === index ? { ...p, ...patch } : p)));
  const pick = (index: number, projectId: string): void => {
    const item = list.items.find((entry) => entry.id === projectId);
    if (item) set(index, { projectId: item.id, code: item.code, name: item.name });
  };
  return (
    <Section title={t('projects.title')} count={t('projects.count', { count: projects.length })}>
      <p className="text-muted-foreground text-sm">{t('projects.hint')}</p>
      {projects.map((project, index) => (
        <div key={`${String(index)}-${project.projectId}`} className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <SearchableSelect
              id={`${id}-p${String(index)}`}
              aria-label={t('projects.which', { n: index + 1 })}
              options={optionsFor(
                list.items,
                project.projectId === ''
                  ? null
                  : { id: project.projectId, code: project.code, name: project.name },
                projects.map((p) => p.projectId),
              )}
              status={list.status}
              value={project.projectId}
              onValueChange={(value) => pick(index, value)}
              onRetry={list.retry}
              placeholder={t('projects.choose')}
              loadingLabel={t('catalog.loading')}
              emptyLabel={t('catalog.empty')}
              errorLabel={t('catalog.unavailable')}
              retryLabel={t('catalog.retry')}
              searchPlaceholder={t('projects.search')}
            />
          </div>
          {projects.length === 1 ? (
            <span
              className="text-muted-foreground w-24 text-right text-sm"
              title={t('projects.fixed')}
            >
              100 %
            </span>
          ) : (
            <span className="flex w-24 items-center gap-1 text-sm">
              <Input
                aria-label={t('projects.share', { n: index + 1 })}
                inputMode="decimal"
                value={project.percent}
                onChange={(event) => set(index, { percent: event.target.value })}
                className="text-right"
              />
              %
            </span>
          )}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={t('projects.remove', { n: index + 1 })}
            onClick={() => onChange(projects.filter((_, i) => i !== index))}
          >
            <X />
          </Button>
        </div>
      ))}
      <SharesLine projects={projects} />
      <div className="flex flex-wrap gap-2">
        {projects.length < MAX_PROJECTS ? (
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              onChange([...projects, { projectId: '', code: '', name: '', percent: '' }])
            }
          >
            <Plus />
            {t('projects.add')}
          </Button>
        ) : (
          <p className="text-muted-foreground text-sm">{t('projects.max')}</p>
        )}
        {projects.length > 1 ? (
          <Button type="button" variant="outline" onClick={() => onChange(splitEqually(projects))}>
            <Split />
            {t('projects.split')}
          </Button>
        ) : null}
      </div>
    </Section>
  );
}

/** «Cliente»: one, optional, searchable (U1 board 1.1). */
export function ClientField({
  allocation,
  list,
  onChange,
}: {
  readonly allocation: Allocation;
  readonly list: CatalogList;
  readonly onChange: (client: Allocation['client']) => void;
}) {
  const { t } = useTranslation('expenses-parity');
  const id = useId();
  const client = allocation.client;
  return (
    <Section title={t('client.title')} count={t('client.optional')}>
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <SearchableSelect
            id={id}
            aria-label={t('client.title')}
            options={optionsFor(
              list.items,
              client ? { id: client.clientId, code: client.code, name: client.name } : null,
              [],
            )}
            status={list.status}
            value={client?.clientId ?? ''}
            onValueChange={(value) => {
              const item = list.items.find((entry) => entry.id === value);
              if (item) onChange({ clientId: item.id, code: item.code, name: item.name });
            }}
            onRetry={list.retry}
            placeholder={t('client.none')}
            loadingLabel={t('catalog.loading')}
            emptyLabel={t('catalog.empty')}
            errorLabel={t('catalog.unavailable')}
            retryLabel={t('catalog.retry')}
            searchPlaceholder={t('client.search')}
          />
        </div>
        {client ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={t('client.remove')}
            onClick={() => onChange(null)}
          >
            <X />
          </Button>
        ) : null}
      </div>
    </Section>
  );
}
